import React, { useCallback, useContext, useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import {
  Alert,
  Box,
  Card,
  Chip,
  CircularProgress,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  Grid,
  Icon,
  IconButton,
  List,
  ListItemButton,
  ListItemText,
  Stack,
  Switch,
  TextField,
  Tooltip,
} from "@mui/material";
import { useNavigate, useParams } from "react-router-dom";
import { ContentState, EditorState, convertFromHTML } from "draft-js";
import { convertToHTML } from "draft-convert";
import { Editor } from "react-draft-wysiwyg";
import "react-draft-wysiwyg/dist/react-draft-wysiwyg.css";

import SoftBox from "components/SoftBox";
import SoftButton from "components/SoftButton";
import SoftTypography from "components/SoftTypography";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import { AuthContext } from "context";
import { documentationService } from "services/documentationService";
import { API_BASE_URL } from "services/config";

const categoryMeta = {
  iaas: { icon: "cloud", color: "#0f766e" },
  saas: { icon: "apps", color: "#0c4a6e" },
  ecm: { icon: "folder", color: "#1d4ed8" },
  epm: { icon: "settings", color: "#854d0e" },
  auto: { icon: "bolt", color: "#9f1239" },
  sboffice: { icon: "business", color: "#4c1d95" },
};

const RESTRICTED_DOCUMENTATION_SLUG = "sboffice";
const RESTRICTED_DOCUMENTATION_ROUTE = `/documentation/${RESTRICTED_DOCUMENTATION_SLUG}`;

const slugify = (value = "") =>
  String(value)
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9\s_-]/g, "")
    .replace(/[\s_]+/g, "-")
    .replace(/-+/g, "-")
    .replace(/^-|-$/g, "")
    .slice(0, 120);

const pathify = (value = "") =>
  String(value)
    .split("/")
    .map((segment) => slugify(segment))
    .filter(Boolean)
    .join("/");

const toEditorState = (html) => {
  const raw = String(html || "");
  if (!raw.trim()) return EditorState.createEmpty();
  const parsed = convertFromHTML(raw);
  if (!parsed || !parsed.contentBlocks || parsed.contentBlocks.length === 0) {
    return EditorState.createEmpty();
  }
  const content = ContentState.createFromBlockArray(parsed.contentBlocks, parsed.entityMap);
  return EditorState.createWithContent(content);
};

const editorStateToHtml = (state) =>
  convertToHTML({
    entityToHTML: (entity, originalText) => {
      if (entity.type === "IMAGE") {
        const src = entity.data?.src || entity.data?.url || entity.data?.link;
        const alt = entity.data?.alt || "";
        return src ? `<img src="${src}" alt="${alt}" style="max-width:100%;height:auto;" />` : originalText;
      }
      if (entity.type === "EMBEDDED_LINK") {
        const src = entity.data?.src;
        return src
          ? `<iframe src="${src}" style="width:100%;min-height:360px;border:0;" allowfullscreen></iframe>`
          : originalText;
      }
      return originalText;
    },
  })(state.getCurrentContent());

function TreeNode({ node, depth, selectedPath, expanded, onToggle, onSelect, isAdmin, onCreate }) {
  const hasChildren = node.children?.length > 0;
  const isOpen = Boolean(expanded[node.full_path]);
  const selected = node.full_path === selectedPath;

  return (
    <>
      <ListItemButton
        selected={selected}
        onClick={() => onSelect(node)}
        sx={{ pl: 1 + depth * 1.5, borderRadius: 1.5, mb: 0.4, minHeight: 38 }}
      >
        {hasChildren ? (
          <IconButton
            size="small"
            onClick={(event) => {
              event.stopPropagation();
              onToggle(node.full_path);
            }}
            sx={{ mr: 0.3 }}
          >
            <Icon fontSize="small">{isOpen ? "expand_more" : "chevron_right"}</Icon>
          </IconButton>
        ) : (
          <Box sx={{ width: 26 }} />
        )}
        <Icon fontSize="small" sx={{ mr: 1, opacity: 0.7 }}>
          {hasChildren ? "folder_open" : "description"}
        </Icon>
        <ListItemText
          primary={node.title}
          primaryTypographyProps={{ noWrap: true, fontSize: "0.9rem", fontWeight: selected ? 700 : 500 }}
          secondary={!node.is_published ? "Entwurf" : null}
          secondaryTypographyProps={{ fontSize: "0.7rem" }}
        />
        {isAdmin && (
          <Tooltip title="Unterseite">
            <IconButton
              size="small"
              onClick={(event) => {
                event.stopPropagation();
                onCreate(node);
              }}
            >
              <Icon fontSize="small">add</Icon>
            </IconButton>
          </Tooltip>
        )}
      </ListItemButton>
      {hasChildren && (
        <Collapse in={isOpen} timeout="auto" unmountOnExit>
          <List disablePadding>
            {node.children.map((child) => (
              <TreeNode
                key={child.id}
                node={child}
                depth={depth + 1}
                selectedPath={selectedPath}
                expanded={expanded}
                onToggle={onToggle}
                onSelect={onSelect}
                isAdmin={isAdmin}
                onCreate={onCreate}
              />
            ))}
          </List>
        </Collapse>
      )}
    </>
  );
}

TreeNode.propTypes = {
  node: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    children: PropTypes.arrayOf(PropTypes.object),
    full_path: PropTypes.string,
    title: PropTypes.string,
    is_published: PropTypes.bool,
  }).isRequired,
  depth: PropTypes.number.isRequired,
  selectedPath: PropTypes.string,
  expanded: PropTypes.objectOf(PropTypes.bool).isRequired,
  onToggle: PropTypes.func.isRequired,
  onSelect: PropTypes.func.isRequired,
  isAdmin: PropTypes.bool.isRequired,
  onCreate: PropTypes.func.isRequired,
};

TreeNode.defaultProps = {
  selectedPath: "",
};

export default function Documentation() {
  const navigate = useNavigate();
  const params = useParams();
  const { user, partnerSettings, customerPrices, isRestrictedPortal } = useContext(AuthContext);

  const productSlug = slugify(params.productSlug || "");
  const pagePath = pathify(params["*"] || "");
  const isWorkspace = Boolean(productSlug);

  const [products, setProducts] = useState([]);
  const [productsLoading, setProductsLoading] = useState(false);
  const [productsError, setProductsError] = useState("");
  const [tree, setTree] = useState([]);
  const [treeLoading, setTreeLoading] = useState(false);
  const [treeError, setTreeError] = useState("");
  const [product, setProduct] = useState(null);
  const [canEditProduct, setCanEditProduct] = useState(false);
  const [page, setPage] = useState(null);
  const [pageLoading, setPageLoading] = useState(false);
  const [pageError, setPageError] = useState("");
  const [expanded, setExpanded] = useState({});

  const [createOpen, setCreateOpen] = useState(false);
  const [createParent, setCreateParent] = useState(null);
  const [createTitle, setCreateTitle] = useState("");
  const [createSlug, setCreateSlug] = useState("");
  const [createSort, setCreateSort] = useState(0);
  const [createPublished, setCreatePublished] = useState(true);
  const [createError, setCreateError] = useState("");
  const [createSaving, setCreateSaving] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleteError, setDeleteError] = useState("");
  const [deleteLoading, setDeleteLoading] = useState(false);

  const [editMode, setEditMode] = useState(false);
  const [editTitle, setEditTitle] = useState("");
  const [editSlug, setEditSlug] = useState("");
  const [editSort, setEditSort] = useState(0);
  const [editPublished, setEditPublished] = useState(true);
  const [editorState, setEditorState] = useState(EditorState.createEmpty());
  const [editHtmlMode, setEditHtmlMode] = useState(false);
  const [htmlSource, setHtmlSource] = useState("");
  const [saveError, setSaveError] = useState("");
  const [saveLoading, setSaveLoading] = useState(false);

  const customNames = useMemo(() => {
    if (user?.isPartner) {
      return partnerSettings?.effective_customer_custom_names || partnerSettings?.custom_names || {};
    }
    return partnerSettings?.custom_names || {};
  }, [user, partnerSettings]);

  const visibleProducts = useMemo(
    () =>
      (isRestrictedPortal ? products.filter((item) => item.slug === RESTRICTED_DOCUMENTATION_SLUG) : products).map((item) => {
        const name = customerPrices?.[item.slug]?.custom_name || customNames[item.slug] || item.name;
        return { ...item, display_name: name };
      }),
    [products, customerPrices, customNames, isRestrictedPortal]
  );

  const loadProducts = useCallback(async () => {
    setProductsLoading(true);
    setProductsError("");
    try {
      const data = await documentationService.getProducts();
      setProducts(Array.isArray(data) ? data : []);
    } catch (error) {
      setProducts([]);
      setProductsError(error?.response?.data?.error || "Produkte konnten nicht geladen werden.");
    } finally {
      setProductsLoading(false);
    }
  }, []);

  const loadTree = useCallback(async (slug) => {
    setTreeLoading(true);
    setTreeError("");
    try {
      const result = await documentationService.getTree(slug);
      setTree(result?.tree || []);
      setProduct(result?.product || null);
      setCanEditProduct(Boolean(result?.can_edit));
    } catch (error) {
      setTree([]);
      setProduct(null);
      setCanEditProduct(false);
      setTreeError(error?.response?.data?.error || "Sitemap konnte nicht geladen werden.");
    } finally {
      setTreeLoading(false);
    }
  }, []);

  const loadPage = useCallback(
    async (slug, path) => {
      setPageLoading(true);
      setPageError("");
      try {
        const result = await documentationService.getPageByPath(slug, path);
        const loadedPage = result?.page || null;
        setPage(loadedPage);
        setCanEditProduct(Boolean(result?.can_edit));
        if (loadedPage?.full_path && loadedPage.full_path !== path) {
          navigate(`/documentation/${slug}/${loadedPage.full_path}`, { replace: true });
        }
      } catch (error) {
        setPage(null);
        if (error?.response?.status === 404) {
          setPageError(path ? "Diese Seite wurde nicht gefunden." : "");
        } else {
          setPageError(error?.response?.data?.error || "Seite konnte nicht geladen werden.");
        }
      } finally {
        setPageLoading(false);
      }
    },
    [navigate]
  );

  useEffect(() => {
    loadProducts();
  }, [loadProducts]);

  useEffect(() => {
    if (!isRestrictedPortal) return;
    if (
      !isWorkspace ||
      productSlug !== RESTRICTED_DOCUMENTATION_SLUG
    ) {
      navigate(RESTRICTED_DOCUMENTATION_ROUTE, { replace: true });
    }
  }, [isRestrictedPortal, isWorkspace, productSlug, navigate]);

  useEffect(() => {
    if (!isWorkspace) {
      setTree([]);
      setProduct(null);
      setCanEditProduct(false);
      setPage(null);
      setEditMode(false);
      return;
    }
    if (
      isRestrictedPortal &&
      productSlug !== RESTRICTED_DOCUMENTATION_SLUG
    ) return;
    loadTree(productSlug);
  }, [isWorkspace, productSlug, loadTree, isRestrictedPortal]);

  useEffect(() => {
    if (!isWorkspace) return;
    if (
      isRestrictedPortal &&
      productSlug !== RESTRICTED_DOCUMENTATION_SLUG
    ) return;
    loadPage(productSlug, pagePath);
  }, [isWorkspace, productSlug, pagePath, loadPage, isRestrictedPortal]);

  useEffect(() => {
    if (!page?.full_path) return;
    let prefix = "";
    setExpanded((prev) => {
      const next = { ...prev };
      page.full_path.split("/").slice(0, -1).forEach((part) => {
        prefix = prefix ? `${prefix}/${part}` : part;
        next[prefix] = true;
      });
      return next;
    });
  }, [page]);

  useEffect(() => {
    if (!page) return;
    setEditTitle(page.title || "");
    setEditSlug(page.slug || "");
    setEditSort(Number.isInteger(page.sort_order) ? page.sort_order : 0);
    setEditPublished(page.is_published !== false);
    setEditorState(toEditorState(page.content_html || ""));
    setHtmlSource(page.content_html || "");
    setEditHtmlMode(false);
    setEditMode(false);
    setSaveError("");
  }, [page]);

  const openCreate = (parent = null) => {
    setCreateParent(parent);
    setCreateTitle("");
    setCreateSlug("");
    setCreateSort(0);
    setCreatePublished(true);
    setCreateError("");
    setCreateOpen(true);
  };

  const createPage = async () => {
    if (!productSlug) return;
    setCreateSaving(true);
    setCreateError("");
    try {
      const created = await documentationService.createPage({
        productSlug,
        parentId: createParent?.id || null,
        title: createTitle,
        slug: createSlug,
        sortOrder: createSort,
        isPublished: createPublished,
        contentHtml: `<h2>${createTitle || "Neue Seite"}</h2><p>Inhalt bearbeiten...</p>`,
      });
      setCreateOpen(false);
      await loadTree(productSlug);
      navigate(`/documentation/${productSlug}/${created.full_path}`);
    } catch (error) {
      setCreateError(error?.response?.data?.error || "Seite konnte nicht erstellt werden.");
    } finally {
      setCreateSaving(false);
    }
  };

  const savePage = async () => {
    if (!page || !productSlug) return;
    setSaveLoading(true);
    setSaveError("");
    try {
      const updated = await documentationService.updatePage(page.id, {
        title: editTitle,
        slug: editSlug,
        sortOrder: editSort,
        isPublished: editPublished,
        contentHtml: editHtmlMode ? htmlSource : editorStateToHtml(editorState),
      });
      setPage(updated);
      setEditMode(false);
      await loadTree(productSlug);
      if (updated.full_path !== page.full_path) {
        navigate(`/documentation/${productSlug}/${updated.full_path}`, { replace: true });
      }
    } catch (error) {
      setSaveError(error?.response?.data?.error || "Seite konnte nicht gespeichert werden.");
    } finally {
      setSaveLoading(false);
    }
  };

  const deleteCurrentPage = async () => {
    if (!page || !productSlug) return;
    setDeleteLoading(true);
    setDeleteError("");
    try {
      await documentationService.deletePage(page.id, true);
      setDeleteOpen(false);
      setPage(null);
      setEditMode(false);
      await loadTree(productSlug);
      navigate(`/documentation/${productSlug}`);
    } catch (error) {
      setDeleteError(error?.response?.data?.error || "Seite konnte nicht geloescht werden.");
    } finally {
      setDeleteLoading(false);
    }
  };

  const uploadImageForEditor = async (file) => {
    const response = await documentationService.uploadImage(file, productSlug);
    const path = response?.url || "";
    const link = path.startsWith("http") ? path : `${API_BASE_URL}${path}`;
    return { data: { link } };
  };

  const WelcomeView = () => (
    <>
      <Card sx={{ mb: 3, borderRadius: 2.5, background: "linear-gradient(120deg, #052e16 0%, #111827 50%, #1e3a8a 100%)" }}>
        <SoftBox p={{ xs: 3, md: 4 }}>
          <SoftTypography variant="h3" color="white" fontWeight="bold">Documentation Hub</SoftTypography>
          <SoftTypography variant="body2" color="white" opacity={0.9} mt={1}>
            Hier koennen Sie sich hilfreiche Anleitung anschauen und Produktfunktionen entdecken!
          </SoftTypography>
        </SoftBox>
      </Card>
      {productsError && <Alert severity="error" sx={{ mb: 2 }}>{productsError}</Alert>}
      {productsLoading ? (
        <SoftBox py={6} display="flex" justifyContent="center"><CircularProgress /></SoftBox>
      ) : (
        <Grid container spacing={3}>
          {visibleProducts.map((item) => {
            const meta = categoryMeta[item.category] || { icon: "description", color: "#334155" };
            return (
              <Grid item xs={12} md={6} lg={4} key={item.slug}>
                <Card sx={{ borderRadius: 2.5, border: `1px solid ${meta.color}30`, boxShadow: "0 16px 34px rgba(15,23,42,.08)" }}>
                  <SoftBox p={3}>
                    <SoftBox sx={{ width: 48, height: 48, borderRadius: 2, backgroundColor: `${meta.color}20`, display: "grid", placeItems: "center", mb: 2 }}>
                      <Icon sx={{ color: meta.color }}>{meta.icon}</Icon>
                    </SoftBox>
                    <SoftTypography variant="h5" fontWeight="bold">{item.display_name}</SoftTypography>
                    <SoftTypography variant="body2" color="text" mt={1}>{item.description || "Produktdokumentation"}</SoftTypography>
                    <Stack direction="row" spacing={1} mt={2} mb={2}>
                      <Chip size="small" label={String(item.category || "").toUpperCase()} />
                      <Chip size="small" label={`${item.page_count || 0} Seiten`} />
                    </Stack>
                    <SoftButton
                      variant="gradient"
                      color="info"
                      fullWidth
                      onClick={() => navigate(`/documentation/${item.slug}`)}
                    >
                      Oeffnen
                    </SoftButton>
                  </SoftBox>
                </Card>
              </Grid>
            );
          })}
        </Grid>
      )}
    </>
  );

  const WorkspaceView = () => (
    <>
      <Card sx={{ mb: 3, borderRadius: 2.5 }}>
        <SoftBox p={2} display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={1}>
          <Stack direction="row" spacing={1} alignItems="center">
            <SoftButton iconOnly variant="text" color="dark" onClick={() => navigate("/documentation")}><Icon>west</Icon></SoftButton>
            <SoftTypography variant="h5" fontWeight="bold">{product?.name || productSlug}</SoftTypography>
            {product?.category && <Chip size="small" label={String(product.category).toUpperCase()} />}
          </Stack>
          {canEditProduct && <SoftButton variant="outlined" color="info" startIcon={<Icon>add</Icon>} onClick={() => openCreate(null)}>Neue Seite</SoftButton>}
        </SoftBox>
      </Card>
      <Grid container spacing={3}>
        <Grid item xs={12} lg={3}>
          <Card sx={{ borderRadius: 2.5, height: "calc(100vh - 210px)", minHeight: "76vh", overflow: "hidden" }}>
            <SoftBox p={2.5} sx={{ height: "100%", overflowY: "auto", pr: 1 }}>
              <SoftTypography variant="h6" fontWeight="bold">Sitemap</SoftTypography>
              <Divider sx={{ my: 1.5 }} />
              {treeError && <Alert severity="error" sx={{ mb: 2 }}>{treeError}</Alert>}
              {treeLoading ? <SoftBox py={5} display="flex" justifyContent="center"><CircularProgress size={26} /></SoftBox> : (
                tree.length === 0 ? (
                  <SoftBox py={4} textAlign="center">
                    <SoftTypography variant="body2" color="text">Noch keine Seiten vorhanden.</SoftTypography>
                    {canEditProduct && <SoftButton sx={{ mt: 2 }} variant="gradient" color="info" onClick={() => openCreate(null)}>Erste Seite</SoftButton>}
                  </SoftBox>
                ) : (
                  <List dense disablePadding>
                    {tree.map((node) => (
                      <TreeNode
                        key={node.id}
                        node={node}
                        depth={0}
                        selectedPath={page?.full_path || pagePath}
                        expanded={expanded}
                        onToggle={(fullPath) => setExpanded((prev) => ({ ...prev, [fullPath]: !prev[fullPath] }))}
                        onSelect={(node) => navigate(`/documentation/${productSlug}/${node.full_path}`)}
                        isAdmin={canEditProduct}
                        onCreate={openCreate}
                      />
                    ))}
                  </List>
                )
              )}
            </SoftBox>
          </Card>
        </Grid>
        <Grid item xs={12} lg={9}>
          <Card sx={{ borderRadius: 2.5, height: "calc(100vh - 210px)", minHeight: "76vh", overflow: "hidden" }}>
            <SoftBox p={3} sx={{ height: "100%", overflowY: "auto", pr: 1.5 }}>
              {pageLoading ? <SoftBox py={9} display="flex" justifyContent="center"><CircularProgress /></SoftBox> : pageError ? (
                <Alert severity="warning">{pageError}</Alert>
              ) : !page ? (
                <SoftTypography variant="body2" color="text">Keine Seite ausgewaehlt.</SoftTypography>
              ) : (
                <>
                  <SoftBox display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={1} mb={2}>
                    <Stack spacing={0.5}>
                      <SoftTypography variant="h4" fontWeight="bold">{page.title}</SoftTypography>
                      <Chip size="small" icon={<Icon sx={{ fontSize: "0.95rem !important" }}>link</Icon>} label={`/documentation/${productSlug}/${page.full_path}`} />
                    </Stack>
                    {canEditProduct && (
                      <Stack direction="row" spacing={1}>
                        {!editMode ? (
                          <>
                            <SoftButton variant="outlined" color="info" startIcon={<Icon>add</Icon>} onClick={() => openCreate(page)}>Unterseite</SoftButton>
                            <SoftButton variant="gradient" color="dark" startIcon={<Icon>edit</Icon>} onClick={() => setEditMode(true)}>Bearbeiten</SoftButton>
                            <SoftButton
                              variant="outlined"
                              color="error"
                              startIcon={<Icon>delete</Icon>}
                              onClick={() => {
                                setDeleteError("");
                                setDeleteOpen(true);
                              }}
                            >
                              Loeschen
                            </SoftButton>
                          </>
                        ) : (
                          <>
                            <SoftButton variant="outlined" color="secondary" onClick={() => setEditMode(false)}>Abbrechen</SoftButton>
                            <SoftButton variant="gradient" color="success" onClick={savePage} disabled={saveLoading}>{saveLoading ? "Speichert..." : "Speichern"}</SoftButton>
                          </>
                        )}
                      </Stack>
                    )}
                  </SoftBox>
                  <Divider sx={{ mb: 2 }} />
                  {editMode ? (
                    <>
                      {saveError && <Alert severity="error" sx={{ mb: 2 }}>{saveError}</Alert>}
                      <Grid container spacing={2} sx={{ mb: 2 }}>
                        <Grid item xs={12} md={6}><TextField label="Titel" size="small" fullWidth value={editTitle} onChange={(event) => { setEditTitle(event.target.value); if (!editSlug) setEditSlug(slugify(event.target.value)); }} /></Grid>
                        <Grid item xs={12} md={6}><TextField label="URL-Slug" size="small" fullWidth value={editSlug} onChange={(event) => setEditSlug(slugify(event.target.value))} /></Grid>
                        <Grid item xs={12} md={4}><TextField label="Sortierung" size="small" type="number" fullWidth value={editSort} onChange={(event) => setEditSort(Number.parseInt(event.target.value, 10) || 0)} /></Grid>
                        <Grid item xs={12} md={8}><FormControlLabel control={<Switch checked={editPublished} onChange={(event) => setEditPublished(event.target.checked)} />} label="Seite veroeffentlicht" /></Grid>
                      </Grid>
                      <Stack direction="row" spacing={1} sx={{ mb: 1.5 }}>
                        <SoftButton
                          size="small"
                          variant={editHtmlMode ? "outlined" : "gradient"}
                          color="info"
                          onClick={() => {
                            if (editHtmlMode) {
                              setEditorState(toEditorState(htmlSource));
                            }
                            setEditHtmlMode(false);
                          }}
                        >
                          Visueller Editor
                        </SoftButton>
                        <SoftButton
                          size="small"
                          variant={editHtmlMode ? "gradient" : "outlined"}
                          color="dark"
                          onClick={() => {
                            if (!editHtmlMode) {
                              setHtmlSource(editorStateToHtml(editorState));
                            }
                            setEditHtmlMode(true);
                          }}
                        >
                          HTML Code
                        </SoftButton>
                      </Stack>
                      {editHtmlMode ? (
                        <TextField
                          fullWidth
                          multiline
                          minRows={18}
                          label="HTML importieren / bearbeiten"
                          value={htmlSource}
                          onChange={(event) => setHtmlSource(event.target.value)}
                          helperText="Hier koennen Sie direkt HTML (inkl. iframe) einfuegen."
                        />
                      ) : (
                        <Box sx={{ "& .rdw-editor-wrapper": { border: "1px solid rgba(148,163,184,.5)", borderRadius: 2 }, "& .rdw-editor-toolbar": { border: 0, borderBottom: "1px solid rgba(148,163,184,.5)" }, "& .rdw-editor-main": { minHeight: 420, p: 1 } }}>
                          <Editor
                            editorState={editorState}
                            onEditorStateChange={setEditorState}
                            toolbar={{
                              options: ["inline", "blockType", "fontSize", "list", "textAlign", "link", "embedded", "image", "history"],
                              embedded: { defaultSize: { height: "360", width: "100%" } },
                              image: {
                                uploadEnabled: true,
                                uploadCallback: uploadImageForEditor,
                                previewImage: true,
                                inputAccept: "image/gif,image/jpeg,image/jpg,image/png,image/svg,image/webp",
                                alt: { present: true, mandatory: false },
                                defaultSize: { height: "auto", width: "100%" },
                              },
                            }}
                          />
                        </Box>
                      )}
                    </>
                  ) : (
                    <Box
                      sx={{
                        "& p": { lineHeight: 1.75 },
                        "& ul, & ol": { pl: 3 },
                        "& img": { maxWidth: "100%", height: "auto", borderRadius: 1.5 },
                        "& iframe": { width: "100%", minHeight: 360, border: 0, borderRadius: 1.5 },
                        "& pre": { p: 1.5, borderRadius: 1.5, backgroundColor: "#0f172a", color: "#f8fafc", overflowX: "auto" },
                      }}
                      dangerouslySetInnerHTML={{ __html: page.content_html || "<p>Kein Inhalt vorhanden.</p>" }}
                    />
                  )}
                </>
              )}
            </SoftBox>
          </Card>
        </Grid>
      </Grid>
    </>
  );

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <SoftBox py={3}>{isWorkspace ? <WorkspaceView /> : <WelcomeView />}</SoftBox>
      <Footer />
      <Dialog open={createOpen} onClose={() => !createSaving && setCreateOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>Neue Dokumentationsseite</DialogTitle>
        <DialogContent>
          {createParent && <Alert severity="info" sx={{ mb: 2 }}>Parent: {createParent.title}</Alert>}
          {createError && <Alert severity="error" sx={{ mb: 2 }}>{createError}</Alert>}
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField label="Titel" size="small" fullWidth value={createTitle} onChange={(event) => { setCreateTitle(event.target.value); if (!createSlug) setCreateSlug(slugify(event.target.value)); }} />
            <TextField label="URL-Slug" size="small" fullWidth value={createSlug} onChange={(event) => setCreateSlug(slugify(event.target.value))} />
            <TextField label="Sortierung" size="small" type="number" fullWidth value={createSort} onChange={(event) => setCreateSort(Number.parseInt(event.target.value, 10) || 0)} />
            <FormControlLabel control={<Switch checked={createPublished} onChange={(event) => setCreatePublished(event.target.checked)} />} label="Sofort veroeffentlichen" />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <SoftButton variant="text" color="secondary" onClick={() => setCreateOpen(false)} disabled={createSaving}>Abbrechen</SoftButton>
          <SoftButton variant="gradient" color="info" onClick={createPage} disabled={createSaving}>{createSaving ? "Erstellt..." : "Seite erstellen"}</SoftButton>
        </DialogActions>
      </Dialog>
      <Dialog open={deleteOpen} onClose={() => !deleteLoading && setDeleteOpen(false)} fullWidth maxWidth="xs">
        <DialogTitle>Seite loeschen</DialogTitle>
        <DialogContent>
          {deleteError && <Alert severity="error" sx={{ mb: 2 }}>{deleteError}</Alert>}
          <SoftTypography variant="body2" color="text">
            Soll die Seite <strong>{page?.title || "diese Seite"}</strong> inklusive Unterseiten wirklich geloescht werden?
          </SoftTypography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <SoftButton variant="text" color="secondary" onClick={() => setDeleteOpen(false)} disabled={deleteLoading}>
            Abbrechen
          </SoftButton>
          <SoftButton variant="gradient" color="error" onClick={deleteCurrentPage} disabled={deleteLoading}>
            {deleteLoading ? "Loescht..." : "Ja, loeschen"}
          </SoftButton>
        </DialogActions>
      </Dialog>
    </DashboardLayout>
  );
}
